import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { JobListingSchema } from '../../validations/job';
import { JobListing } from '../../types/jobs';
import TagInput from '../common/TagInput';
import { BtnDefault, BtnPrimary } from '../common/Buttons';
import { useThemeStore } from '../../state/theme';
import { ArrowLeft } from 'lucide-react';

interface JobListingFormProps {
  onSubmit: () => void;
}

const JobListingModal: React.FC<JobListingFormProps> = ({ onSubmit }) => {
  const navigate = useNavigate();
  const { isDarkMode } = useThemeStore();
  const form = useForm<JobListing>({
    resolver: zodResolver(JobListingSchema),
    defaultValues: {
      title: '',
      openings: 1,
      skills: [],
      requirements: [],
      whatYouBring: [],
      perks: [],
      testUrl: '',
      tags: [],
      keywords: [],
      thresholdScore: '',
    },
  });

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = form;

  const inputClassName = `w-full rounded-md px-3 py-2 shadow-sm focus:outline-none ${
    isDarkMode
      ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-indigo-500 focus:ring-indigo-500'
      : 'border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
  }`;

  const labelClassName = `block text-sm font-medium mb-1 ${
    isDarkMode ? 'text-gray-300' : 'text-gray-700'
  }`;

  const skillTags = watch('skills');
  const hashtags = watch('tags');
  const keywordTags = watch('keywords');
  const onEnterSkill = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const input = e.target as HTMLInputElement;
      const tag = input.value.trim();

      if (e.key === 'Enter' && tag) {
        e.preventDefault();
        const currentTags = getValues('skills') || [];
        setValue('skills', [...currentTags, tag]);
        input.value = '';
      }
    },
    [getValues, setValue]
  );

  const onRemoveSkill = useCallback(
    (index: number) => {
      const tags = getValues('skills') || [];
      setValue(
        'skills',
        tags.filter((_, i) => i !== index)
      );
    },
    [getValues, setValue]
  );

  const onEnterKeyword = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const input = e.target as HTMLInputElement;
      const tag = input.value.trim();

      if (e.key === 'Enter' && tag) {
        e.preventDefault();
        const currentTags = getValues('keywords') || [];
        setValue('keywords', [...currentTags, tag]);
        input.value = '';
      }
    },
    [getValues, setValue]
  );

  const onRemoveKeyword = useCallback(
    (index: number) => {
      const tags = getValues('keywords') || [];
      setValue(
        'keywords',
        tags.filter((_, i) => i !== index)
      );
    },
    [getValues, setValue]
  );

  const onEnterHashtag = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const input = e.target as HTMLInputElement;
      const tag = input.value.trim();

      if (e.key === 'Enter' && tag) {
        e.preventDefault();
        const currentTags = getValues('tags') || [];
        setValue('tags', [...currentTags, `#${tag}`]);
        input.value = '';
      }
    },
    [getValues, setValue]
  );

  const onRemoveHashtag = useCallback(
    (index: number) => {
      const tags = getValues('tags') || [];
      setValue(
        'tags',
        tags.filter((_, i) => i !== index)
      );
    },
    [getValues, setValue]
  );

  return (
    <div>
      <BtnDefault
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 my-4"
      >
        <ArrowLeft size={14} />
        Back
      </BtnDefault>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-5 bg-white p-4 rounded-lg shadow-sm border"
      >
        <div>
          <label htmlFor="title" className={labelClassName}>
            Job Title
          </label>
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <input
                id="title"
                type="text"
                {...field}
                className={inputClassName}
              />
            )}
          />
          {errors.title && (
            <span className="text-red-500 text-sm">{errors.title.message}</span>
          )}
        </div>

        <div>
          <label htmlFor="openings" className={labelClassName}>
            Openings
          </label>
          <Controller
            name="openings"
            control={control}
            render={({ field }) => (
              <input
                id="openings"
                type="number"
                {...field}
                className={inputClassName}
              />
            )}
          />
          {errors.openings && (
            <span className="text-red-500 text-sm">
              {errors.openings.message}
            </span>
          )}
        </div>

        <div>
          <label className={labelClassName}>Skills</label>
          <TagInput
            placeholder="Add skill and press Enter"
            onEnter={onEnterSkill}
            tags={skillTags}
            onRemoveTag={onRemoveSkill}
          />
        </div>

        {(['requirements', 'whatYouBring', 'perks'] as const).map((key) => (
          <div key={key}>
            <label className={labelClassName}>
              {key === 'whatYouBring'
                ? 'What You Bring'
                : key.charAt(0).toUpperCase() + key.slice(1)}
            </label>
            <Controller
              name={key}
              control={control}
              render={({ field }) => (
                <textarea
                  {...field}
                  placeholder={`List ${key}`}
                  value={field.value?.join('\n') || ''}
                  onChange={(e) => field.onChange(e.target.value.split('\n'))}
                  className={inputClassName}
                  rows={5}
                />
              )}
            />
          </div>
        ))}

        <div>
          <label className={labelClassName}>Keywords</label>
          <TagInput
            placeholder="Add keyword and press Enter"
            onEnter={onEnterKeyword}
            tags={keywordTags}
            onRemoveTag={onRemoveKeyword}
          />
        </div>

        <div>
          <label className={labelClassName}>Hashtags</label>
          <TagInput
            placeholder="Add hashtag and press Enter"
            onEnter={onEnterHashtag}
            tags={hashtags}
            onRemoveTag={onRemoveHashtag}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="testUrl" className={labelClassName}>
              Test URL
            </label>
            <Controller
              name="testUrl"
              control={control}
              render={({ field }) => (
                <input
                  id="testUrl"
                  type="text"
                  {...field}
                  className={inputClassName}
                />
              )}
            />
            {errors.testUrl && (
              <span className="text-red-500 text-sm">
                {errors.testUrl.message}
              </span>
            )}
          </div>

          <div>
            <label htmlFor="thresholdScore" className={labelClassName}>
              Threshold Score
            </label>
            <Controller
              name="thresholdScore"
              control={control}
              render={({ field }) => (
                <input
                  id="thresholdScore"
                  type="text"
                  {...field}
                  className={inputClassName}
                />
              )}
            />
            {errors.thresholdScore && (
              <span className="text-red-500 text-sm">
                {errors.thresholdScore.message}
              </span>
            )}
          </div>
        </div>

        <div className="flex justify-end">
          <BtnPrimary type="submit">Next</BtnPrimary>
        </div>
      </form>
    </div>
  );
};

export default JobListingModal;
